import { dataServices, services } from '@ambita/ambita-components-core';
import { ambita, AmbitaMap, AmbitaMapOptions, Icon } from '@ambita/ambita-map';
import { TagInterface } from 'riot';
import { Feature, FeatureCollection, LineString, Polygon } from 'geojson';
import * as commonConstants from '../../constants/common';
import { fetchGeoToken } from '../../services/common';
import { FooterType, setFooterType } from '../../services/integration';
import * as ReportMap from '../../typings/ReportMap';

Icon.Default.imagePath = './images';

const hasPlotIds = (features: Feature<Polygon>[]): boolean => {
  return features.every(feature => feature.properties.teigid);
};

const groupNeighborListPlotIds = (features: Feature<Polygon>[]): ReportMap.GroupedNeighborListPlotIds => {
  let mainCadastrePlotId = 0;
  const neighborCadastrePlotIds = [];

  features.forEach(feature => {
    if (feature.properties && feature.properties.mainCadastre) {
      mainCadastrePlotId = feature.properties.teigid;
    } else {
      neighborCadastrePlotIds.push(feature.properties.teigid);
    }
  });

  return { mainCadastrePlotId, neighborCadastrePlotIds };
};

const groupOwnerListPlotIds = (features: Feature<Polygon>[]): number[] => {
  const ownerCadastrePlotIds = [];

  features.forEach(feature => {
    ownerCadastrePlotIds.push(feature.properties.teigid);
  });

  return ownerCadastrePlotIds;
};

const filterFeatureCollectionFeatures = (
  featureCollection: FeatureCollection<Polygon>,
  filter: string
): ReportMap.FeatureCollectionsWithFilteredFeatures => {
  const originalFeatureCollection = services.util.clone(featureCollection);
  const filteredFeatureCollection = services.util.clone(featureCollection);

  originalFeatureCollection.features = featureCollection.features.filter(feature => !feature.properties[filter]);
  filteredFeatureCollection.features = featureCollection.features.filter(feature => feature.properties[filter]);

  return { originalFeatureCollection, filteredFeatureCollection };
};

const fitBoundsWithBuffer = (
  mapInstance: AmbitaMap,
  featureCollection: FeatureCollection<Polygon>,
  distance: number
): void => {
  const featureCollectionWithBuffer = ambita.Util.buffer(featureCollection, { distance });

  mapInstance.fitBoundsToGeoJSON(featureCollectionWithBuffer, {
    fitBuffer: 0
  });
};

const baseAndPropertyLayerLoadedCallback = (mapInstance: AmbitaMap, onDone: () => void): void => {
  let baseLayerLoaded = false;
  let filteredPropertyLayerLoaded = false;

  const onDoneLoader = () => {
    if (baseLayerLoaded && filteredPropertyLayerLoaded) {
      onDone();
    }
  };

  mapInstance.propertyLayer.once('filteredPropertyLayerLoaded', () => {
    filteredPropertyLayerLoaded = true;
    onDoneLoader();
  });

  mapInstance.baseLayer.on('load', () => {
    baseLayerLoaded = true;
    onDoneLoader();
  });
};

const showNeighborListFeaturesWithPlotIds = (
  mapInstance: AmbitaMap,
  featureCollection: FeatureCollection<Polygon>,
  onDone: () => void
): void => {
  const { mainCadastrePlotId, neighborCadastrePlotIds } = groupNeighborListPlotIds(featureCollection.features);

  baseAndPropertyLayerLoadedCallback(mapInstance, onDone);

  mapInstance.showNeighborListUsingPlotIds(mainCadastrePlotId, neighborCadastrePlotIds, {
    mainPlotOptions: {
      scaleBufferMeters: commonConstants.MAIN_CADASTRE_BUFFER_METERS
    }
  });
};

const showNeighborListFeaturesWithoutPlotIds = (
  mapInstance: AmbitaMap,
  featureCollection: FeatureCollection<Polygon>,
  onDone: () => void
): void => {
  const mainCadastreLayerOptions = {
    svgPathClassName: 'selection-main-cadastre',
    fitToFeature: false
  };

  const neighborCadastreLayerOptions = {
    svgPathClassName: 'selection-neighbor-cadastre',
    fitToFeature: false
  };

  const { originalFeatureCollection, filteredFeatureCollection } =
    filterFeatureCollectionFeatures(featureCollection, commonConstants.FILTER_PROPERTY_NEIGHBOR_LIST);

  mapInstance.baseLayer.on('load', () => {
    onDone();
  });

  fitBoundsWithBuffer(
    mapInstance,
    filteredFeatureCollection,
    ambita.Util.calculateScaleBuffer(featureCollection, commonConstants.MAIN_CADASTRE_BUFFER_METERS)
  );

  mapInstance.showFeatureLayer(filteredFeatureCollection, '', mainCadastreLayerOptions);
  mapInstance.showFeatureLayer(originalFeatureCollection, '', neighborCadastreLayerOptions);
};

const showNeighborListFeatures = (
  mapInstance: AmbitaMap,
  featureCollection: FeatureCollection<Polygon>,
  onDone: () => void
): void => {
  if (hasPlotIds(featureCollection.features)) {
    showNeighborListFeaturesWithPlotIds(mapInstance, featureCollection, onDone);
  } else {
    showNeighborListFeaturesWithoutPlotIds(mapInstance, featureCollection, onDone);
  }
};

const showOwnerListFeatures = (
  mapInstance: AmbitaMap,
  featureCollection: FeatureCollection<Polygon>,
  onDone: () => void
): void => {
  const { originalFeatureCollection, filteredFeatureCollection } =
    filterFeatureCollectionFeatures(featureCollection, commonConstants.FILTER_PROPERTY_OWNER_LIST);

  const isPolylineSelection = filteredFeatureCollection && filteredFeatureCollection.features &&
    filteredFeatureCollection.features.some((feature: Feature<Polygon> | Feature<LineString>) => {
      return feature.geometry.type === 'LineString';
    });

  const baseClassName = 'ownerlist-original-selection';
  const selectionClassName = `${baseClassName}${isPolylineSelection ? '--line' : '--polygon'}`;
  const selectionLayerOptions = {
    svgPathClassName: `${baseClassName} ${selectionClassName}`,
    fitToFeature: false
  };

  const ownerListLayerOptions = {
    fitToFeature: false
  };

  if (hasPlotIds(originalFeatureCollection.features)) {
    baseAndPropertyLayerLoadedCallback(mapInstance, onDone);

    mapInstance.filterCadastreLabelsOnPropertyLayer(groupOwnerListPlotIds(originalFeatureCollection.features));
  } else {
    mapInstance.baseLayer.on('load', () => {
      onDone();
    });
  }

  fitBoundsWithBuffer(mapInstance, filteredFeatureCollection, commonConstants.SELECTION_BUFFER_METERS);

  mapInstance.showFeatureLayer(originalFeatureCollection, '', ownerListLayerOptions);
  mapInstance.showFeatureLayer(filteredFeatureCollection, '', selectionLayerOptions);
};

const getDefaultMapOptions = (): AmbitaMapOptions => {
  return {
    static: true,
    zoom: 3,
    zoomSnap: 0.1,
    scaleControl: true,
    fullscreenControl: false,
    zoomControl: false,
    layerControl: false,
    showPropertyLayer: true,
    poweredByControl: false,
    restApi: dataServices.config.getConfig().API_HOST
  };
};

const runTask = (mapInstance: AmbitaMap, taskConfig: ReportMap.MapTaskConfig): void => {
  if (taskConfig.type === commonConstants.TASK_NEIGHBOR_LIST) {
    setFooterType(FooterType.none);
    showNeighborListFeatures(mapInstance, taskConfig.geojson, taskConfig.onTaskDone);
  } else if (taskConfig.type === commonConstants.TASK_OWNER_LIST) {
    showOwnerListFeatures(mapInstance, taskConfig.geojson, taskConfig.onTaskDone);
  }
};

export const init = (tag: TagInterface): void => {
  tag.on('mount', () => {
    fetchGeoToken().then((geoToken) => {
      const mapOptions = { ...getDefaultMapOptions(), ...tag.opts.config };
      mapOptions.geoToken = geoToken.token;

      const mapInstance = ambita.map('map', null, mapOptions);

      if (tag.opts.task) {
        runTask(mapInstance, tag.opts.task);
      }
    });
  });
};
